import type { IClientGroupView } from "../../src/taskpane/services/clientGroupService";
import { clientGroupSearchSites, IClientGroupSearchSite } from "../../src/taskpane/components/helpers/sharepointSearch/clientGroupSearchScope";
import { ILocation } from "../Services/locationTypes";
import { mapGraphSiteToLocation } from "../Services/siteLocationMapping";
import { ILocationSection } from "./locationSections";

export interface IClientGroupGridSections {
    /** The site's own members, as rows of the "Client group" section. */
    memberRows: ILocation[];
    /** One section per group the site belongs to: the group's own site first, then the other members. */
    parentSections: ILocationSection<ILocation>[];
    /** Every group site for the All documents view (members listed elsewhere too), in the same
     * order as Advanced Search. */
    aggregateSites: IClientGroupSearchSite[];
}

export const NO_CLIENT_GROUP_SECTIONS: IClientGroupGridSections = Object.freeze({ memberRows: [], parentSections: [], aggregateSites: [] }) as IClientGroupGridSections;

/** Whether there is anything to show: a row, a section or a group site for All documents. */
export const hasClientGroupSections = (sections: IClientGroupGridSections): boolean =>
    sections.memberRows.length > 0 || sections.parentSections.length > 0 || sections.aggregateSites.length > 0;

/**
 * The locations grid's client group sections for one site, from the lookup's answer. Members
 * already listed as hub children or real subsites stay where they are and are left out here;
 * a section left empty by that is dropped. Row keys carry the section, so no two collide.
 */
export const buildClientGroupGridSections = (
    view: IClientGroupView | undefined,
    listedSites: ({ id?: string } | undefined)[],
    currentSiteId: string | undefined,
): IClientGroupGridSections => {
    const listedSiteIds: Set<string> = new Set<string>((listedSites ?? []).map(listed => listed?.id?.toLowerCase()));
    const isListedElsewhere = (site: { id?: string } | undefined): boolean => !site?.id || listedSiteIds.has(site.id.toLowerCase());

    const memberRows: ILocation[] = (view?.members ?? [])
        .filter(member => !isListedElsewhere(member))
        .map((member, index) => ({ ...mapGraphSiteToLocation(member, index), key: `g${index}` }));
    const parentSections: ILocationSection<ILocation>[] = (view?.groups ?? [])
        .map((group, groupIndex) => ({
            key: String(group.itemId),
            name: group.label,
            items: [group.site, ...(group.members ?? [])]
                .filter(site => !isListedElsewhere(site))
                .map((site, index) => ({ ...mapGraphSiteToLocation(site, index), key: `p${groupIndex}-${index}` }))
        }))
        .filter(section => section.items.length > 0);

    return { memberRows, parentSections, aggregateSites: clientGroupSearchSites(view, currentSiteId) };
};
